import {
  useContext,
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

import useAbortSignal from "../hooks/useAbortSignal";
import fetchWrapper from "../lib/apiCall";
import { successfulToast } from "../util/toastNotifications";
import { useAppData } from "./AppDataContext";
import { useAuthInfo } from "./AuthContext";

const TimeTrackingContext = createContext();

export const TimeTrackingContextProvider = ({ children }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [groupedTimeEntries, setGroupedTimeEntries] = useState(null);
  const [isAddingTime, setIsAddingTime] = useState(false);
  const [runningBillable, setRunningBillable] = useState(false);
  const [runningNonBillable, setRunningNonBillable] = useState(false);
  const [runningEntry, setRunningEntry] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [taskId, setTaskId] = useState("");
  const [time, setTime] = useState("00:00:00");
  const [previousTaskInfo, setPreviousTaskInfo] = useState(null);
  const [digits, setDigits] = useState({
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  const { allAssignees = [], api } = useAppData();
  const { userInfo } = useAuthInfo();
  const { pathname } = useLocation();
  const { signal } = useAbortSignal();

  const allAssigneesLength = useMemo(
    () => allAssignees.length,
    [allAssignees.length]
  );

  const handleActivityData = useCallback((data) => {
    setActivityData(data);
  }, []);

  const dismissPreviousTaskNotification = useCallback(() => {
    setPreviousTaskInfo(null);
  }, []);

  const handleDataEntry = useCallback((field, value) => {
    switch (field) {
      case "groupedTimeEntries":
        setGroupedTimeEntries(value);
        break;

      case "isAddingTime":
        setIsAddingTime(value);
        break;

      case "time":
        setTime(value);
        break;

      case "digits":
        setDigits(value);
        break;

      case "taskId":
        setTaskId(value);
        break;

      default:
        break;
    }
  }, []);

  const fetchTimeEntries = useCallback(() => {
    if (taskId) {
      setTimeout(async () => {
        try {
          const response = await fetchWrapper.apiCall(
            `/time-entry/task/${taskId}`,
            "GET",
            null,
            null,
            signal
          );
          const results = response.results || [];
          if (results.length > 0) {
            let refreshedEntries = results.sort((a, b) => {
              let a_start = new Date(a.date_started).getTime();
              let b_start = new Date(b.date_started).getTime();

              const a_end = new Date(a.date_ended).getTime();
              const b_end = new Date(b.date_ended).getTime();

              if (a_end > b_end) {
                a_start += 1;
              } else {
                b_start += 1;
              }

              return a_start - b_start;
            });

            refreshedEntries = refreshedEntries.filter((entry) => {
              return (
                entry.date_ended &&
                entry.date_started &&
                new Date(entry.date_ended).getTime() !==
                  new Date(entry.date_started).getTime()
              );
            });

            setTimeEntries(refreshedEntries);
          } else {
            setTimeEntries([]);
          }
        } catch (err) {
          console.error("Time Entries GET Error: ", err.message);
        }
      }, 1000);
    }
  }, [taskId, signal]);

  const handleTimeStartStop = useCallback(
    async (id, now, endDatetimeBody, doStop, doToast) => {
      if (taskId) {
        if (id === "billable" || id === "non-billable") {
          if (doToast)
            successfulToast(
              "You were tracking time on a previous task. Time tracking for that task has been stopped and Time Tracking has begun on this task."
            );

          const body = {
            org_id: userInfo.org_id,
            user_id: userInfo.user_id,
            entered_by_id: userInfo.user_id,
            task_id: taskId,
            date_started: now,
            date_ended: now,
            billable: id === "billable" ? true : false,
          };

          try {
            const data = await fetchWrapper.apiCall(
              "/time-entry",
              "POST",
              body,
              null,
              signal
            );
            if (id === "billable") setRunningBillable(true);
            if (id === "non-billable") setRunningNonBillable(true);
            if (Object.values(data.activity_log || {}).length > 0)
              setActivityData(data.activity_log);

            setRunningEntry(data.results);
          } catch (err) {
            console.error("Time Entry START error: ", err);
          }
        } else if (doStop && (runningBillable || runningNonBillable)) {
          try {
            const data = await fetchWrapper.apiCall(
              `/time-entry/${runningEntry.time_entry_id}`,
              "PUT",
              endDatetimeBody,
              null,
              signal
            );
            setRunningBillable(false);
            setRunningNonBillable(false);
            setRunningEntry(null);
            setActivityData(data.activity_log);
          } catch (err) {
            console.error("Time Entry STOP error: ", err);
          }
        }
      }
    },
    [
      signal,
      taskId,
      userInfo?.org_id,
      userInfo?.user_id,
      runningBillable,
      runningEntry?.time_entry_id,
      runningNonBillable,
    ]
  );

  const isTrackingInFlightRef = useRef(false);

  const handleTimeTracking = useCallback(
    async (e, id) => {
      e.preventDefault();

      if (isTrackingInFlightRef.current) return;
      isTrackingInFlightRef.current = true;

      const now = new Date().toISOString();

      const endDatetimeBody = {
        date_ended: now,
      };

      try {
        if (id.startsWith("stop-") && runningEntry) {
          await handleTimeStartStop(id, now, endDatetimeBody, true);
          return;
        }

        const userTimeEntriesResponse = await fetchWrapper.apiCall(
          `/time-entry/user/${userInfo.user_id}`,
          "GET",
          null,
          null,
          signal
        );
        const userTimeEntries = userTimeEntriesResponse.results || [];

        const runningTimeEntry = userTimeEntries.find(
          (entry) =>
            entry.date_ended &&
            entry.date_started &&
            new Date(entry.date_ended).getTime() ===
              new Date(entry.date_started).getTime()
        );

        if (
          !runningTimeEntry ||
          (runningEntry &&
            runningTimeEntry.time_entry_id === runningEntry.time_entry_id)
        ) {
          await handleTimeStartStop(id, now, endDatetimeBody, true);
        } else {
          const previousTaskId = runningTimeEntry.task_id;
          let previousTaskName = "Unknown Task";

          if (api && previousTaskId) {
            try {
              const taskData = await api.task.getTaskById(previousTaskId);
              previousTaskName = taskData?.name || previousTaskName;
            } catch (err) {
              console.error("Error fetching previous task info:", err);
            }
          }

          try {
            await fetchWrapper.apiCall(
              `/time-entry/${runningTimeEntry.time_entry_id}`,
              "PUT",
              endDatetimeBody,
              null,
              signal
            );
            setRunningBillable(false);
            setRunningNonBillable(false);
            setRunningEntry(null);

            if (previousTaskId && previousTaskId !== taskId) {
              setPreviousTaskInfo({
                task_id: previousTaskId,
                task_name: previousTaskName,
              });
            }

            handleTimeStartStop(id, now, endDatetimeBody, false, true);
          } catch (err) {
            console.error("Time Entry STOP error: ", err);
          }
        }
      } catch (err) {
        console.error("Running Time Check error: ", err);
      } finally {
        isTrackingInFlightRef.current = false;
      }
    },
    [signal, runningEntry, handleTimeStartStop, userInfo?.user_id, taskId, api]
  );

  const handleCalculateTotalTaskTime = useCallback(
    (isBillable = "") => {
      let result = "00:00:00";

      if (timeEntries.length > 0) {
        let recordArr = timeEntries;

        if (isBillable === "billable")
          recordArr = recordArr.filter((entry) => entry.billable);
        if (isBillable === "non-billable")
          recordArr = recordArr.filter((entry) => !entry.billable);

        const secondsArr = recordArr.reduce((acc, cV) => {
          const start_date = new Date(cV.date_started);
          const end_date = new Date(cV.date_ended);

          let seconds_diff = (end_date.getTime() - start_date.getTime()) / 1000;

          acc.push(seconds_diff);
          return acc;
        }, []);

        const totalSeconds = secondsArr.reduce((acc, cV) => acc + cV, 0);

        const totalMinutes = Math.floor(totalSeconds / 60);

        let seconds = Math.floor(totalSeconds % 60);
        let hours = Math.floor(totalMinutes / 60);
        let minutes = totalMinutes % 60;

        if (hours < 10) hours = `0${hours}`;
        if (minutes < 10) minutes = `0${minutes}`;
        if (seconds < 10) seconds = `0${seconds}`;

        result = `${hours}:${minutes}:${seconds}`;
      }

      return result;
    },
    [timeEntries]
  );

  const handleCalculateSingleEntryTime = useCallback(
    (entryRecord, live = false) => {
      const start_date = new Date(entryRecord.date_started);
      let end_date = new Date(entryRecord.date_ended);

      if (live || end_date.getTime() === start_date.getTime()) {
        end_date = new Date();
      }

      const totalSeconds = (end_date.getTime() - start_date.getTime()) / 1000;

      const totalMinutes = Math.floor(totalSeconds / 60);

      let seconds = Math.floor(totalSeconds % 60);
      let hours = Math.floor(totalMinutes / 60);
      let minutes = totalMinutes % 60;

      if (hours < 10) hours = `0${hours}`;
      if (minutes < 10) minutes = `0${minutes}`;
      if (seconds < 10) seconds = `0${seconds}`;

      return `${hours}:${minutes}:${seconds}`;
    },
    []
  );

  useEffect(() => {
    setTaskId(() => {
      if (pathname && pathname.split("/")[1] === "task") {
        return pathname.split("/")[2];
      } else {
        return "";
      }
    });
  }, [pathname]);

  useEffect(() => {
    if (runningEntry) {
      const runningTime = handleCalculateSingleEntryTime(runningEntry, true);
      const splitTime = runningTime.split(":");
      if (splitTime.length === 3) {
        setDigits({
          hours: splitTime[0],
          minutes: splitTime[1],
          seconds: splitTime[2],
        });
      } else {
        setDigits({
          hours: "00",
          minutes: splitTime[0],
          seconds: splitTime[1],
        });
      }
      setTime(runningTime);
    } else {
      setDigits({ hours: "00", minutes: "00", seconds: "00" });
      const totalTime = handleCalculateTotalTaskTime();
      setTime(totalTime || "00:00:00");
    }
  }, [
    runningEntry,
    handleCalculateSingleEntryTime,
    handleCalculateTotalTaskTime,
  ]);

  useEffect(() => {
    if (taskId) {
      setRunningEntry(null);
      setRunningBillable(false);
      setRunningNonBillable(false);

      const fetchEntries = async () => {
        try {
          const response = await fetchWrapper.apiCall(
            `/time-entry/task/${taskId}`,
            "GET",
            null,
            null,
            signal
          );
          const results = response.results || [];
          if (results.length > 0) {
            let refreshedEntries = results.sort((a, b) => {
              let a_start = new Date(a.date_started).getTime();
              let b_start = new Date(b.date_started).getTime();

              const a_end = new Date(a.date_ended).getTime();
              const b_end = new Date(b.date_ended).getTime();

              if (a_end > b_end) {
                a_start += 1;
              } else {
                b_start += 1;
              }

              return a_start - b_start;
            });

            let isRunning = false;
            const runningEntries = refreshedEntries.filter((entry) => {
              return (
                entry.date_ended &&
                entry.date_started &&
                new Date(entry.date_ended).getTime() ===
                  new Date(entry.date_started).getTime() &&
                entry.user_id === userInfo.user_id
              );
            });

            if (runningEntries.length > 0) {
              const currentRunningEntry = runningEntries[0];
              if (currentRunningEntry.task_id === taskId) {
                if (currentRunningEntry.billable) {
                  setRunningBillable(true);
                  setRunningNonBillable(false);
                  setRunningEntry(currentRunningEntry);
                  isRunning = true;
                } else {
                  setRunningBillable(false);
                  setRunningNonBillable(true);
                  setRunningEntry(currentRunningEntry);
                  isRunning = true;
                }
              } else {
                setRunningEntry(null);
                setRunningBillable(false);
                setRunningNonBillable(false);
              }
            } else {
              setRunningEntry(null);
              setRunningBillable(false);
              setRunningNonBillable(false);
            }

            refreshedEntries = refreshedEntries.filter((entry) => {
              return !(
                entry.date_ended &&
                entry.date_started &&
                new Date(entry.date_ended).getTime() ===
                  new Date(entry.date_started).getTime()
              );
            });

            setTimeEntries(refreshedEntries);
          } else {
            setTimeEntries([]);
            setRunningEntry(null);
            setRunningBillable(false);
            setRunningNonBillable(false);
          }
        } catch (err) {
          console.error("Time Entries GET error: ", err.message);
        }
      };
      fetchEntries();
    } else {
      setRunningEntry(null);
      setRunningBillable(false);
      setRunningNonBillable(false);
    }
  }, [isAddingTime, taskId, signal, userInfo?.user_id]);

  useEffect(() => {
    let groupedEntries = {};
    if (allAssigneesLength > 0 && timeEntries.length > 0) {
      groupedEntries = timeEntries.reduce((acc, cV) => {
        acc[cV.user_id] = acc[cV.user_id] || [];
        acc[cV.user_id].push(cV);
        return acc;
      }, {});
    }

    setGroupedTimeEntries(groupedEntries);
  }, [allAssigneesLength, timeEntries]);

  useEffect(() => {
    let timeInterval;

    if (runningEntry) {
      timeInterval = setInterval(() => {
        setDigits((prevDigits) => {
          let hours = Number(prevDigits.hours);
          let minutes = Number(prevDigits.minutes);
          let seconds = Number(prevDigits.seconds);

          if (seconds >= 59) {
            seconds = 0;
            minutes += 1;
          } else {
            seconds += 1;
          }

          if (minutes > 59) {
            minutes = 0;
            hours += 1;
          }

          if (hours > 99) {
            hours = 0;
          }

          const newHours = hours < 10 ? `0${hours}` : String(hours);
          const newMinutes = minutes < 10 ? `0${minutes}` : String(minutes);
          const newSeconds = seconds < 10 ? `0${seconds}` : String(seconds);

          setTime(`${newHours}:${newMinutes}:${newSeconds}`);

          return {
            hours: newHours,
            minutes: newMinutes,
            seconds: newSeconds,
          };
        });
      }, 1000);

      return () => clearInterval(timeInterval);
    }
  }, [runningEntry]);

  const fetchTimeEntriesRef = useRef(fetchTimeEntries);
  useEffect(() => {
    fetchTimeEntriesRef.current = fetchTimeEntries;
  }, [fetchTimeEntries]);

  useEffect(() => {
    if (!runningEntry) {
      fetchTimeEntriesRef.current();
    }
  }, [runningEntry]);

  const values = {
    activityData,
    groupedTimeEntries,
    isAddingTime,
    time,
    digits,
    runningEntry,
    runningBillable,
    runningNonBillable,
    timeEntries,
    previousTaskInfo,
    fetchTimeEntries,
    handleCalculateTotalTaskTime,
    handleCalculateSingleEntryTime,
    handleDataEntry,
    handleTimeTracking,
    handleActivityData,
    dismissPreviousTaskNotification,
  };

  return (
    <TimeTrackingContext.Provider value={values}>
      {children}
    </TimeTrackingContext.Provider>
  );
};

export const useTimeTracking = () => {
  return useContext(TimeTrackingContext);
};
